import { clamp as doClamp } from '@helpers/utils';
import * as React from 'react';
import { useResize } from './useResize';
export function useCarousel(params) {
    const refContainer = React.useRef(null);
    const refTrack = React.useRef(null);
    const [carouselState, dispatch] = React.useReducer(carouselReducer, {
        activeIndex: 0,
        maxIndex: 0,
        offsets: [],
        count: 0,
        paddingOffset: 0,
        scrollByVisibleCount: false,
    });
    useResize({
        callback: initCarousel,
        debounce: 100,
        immediate: true,
        on: 'x',
    });
    React.useEffect(() => {
        initCarousel();
    }, [carouselState.count, carouselState.paddingOffset]);
    const visibleItemCount = getVisibleItemCount();
    const dotCount = params.scrollByVisibleCount
        ? Math.ceil(carouselState.count / visibleItemCount)
        : carouselState.count;
    const activeDotIndex = params.scrollByVisibleCount
        ? Math.floor(carouselState.activeIndex / visibleItemCount)
        : carouselState.activeIndex;
    return {
        ...carouselState,
        goToIndex,
        goToNext,
        goToPrev,
        reinitialise,
        recalculate: initCarousel,
        offset: carouselState.offsets[carouselState.activeIndex],
        refContainer,
        refTrack,
        count: carouselState.count,
        paddingOffset: carouselState.paddingOffset,
        dotCount,
        activeDotIndex,
        visibleItemCount,
        scrollByVisibleCount: params.scrollByVisibleCount ?? false,
    };
    /**
     * Calculate the px offset for the track for the target item index.
     * @param index
     * @returns
     */
    function calculateOffset(index) {
        if (!refTrack.current) {
            return 0;
        }
        const items = Array.from(refTrack.current.children);
        const itemsUntilIndex = items.slice(0, index);
        const sum = itemsUntilIndex
            .map((el) => el.getBoundingClientRect().width)
            .reduce((p, c) => p + c, 0);
        return sum;
    }
    /**
     * Initialise the reducer state.
     */
    function initCarousel() {
        const offsets = [];
        // Pre-calculate the px offset for each item in the carousel.
        Array.from(Array(params.count)).forEach((_, index) => {
            offsets.push(calculateOffset(index));
        });
        // Determine what the max index value needs to be in order for the last item to be within view.
        const containerWidth = (refContainer.current?.getBoundingClientRect().width ?? 0) - (params.paddingOffset ?? 0);
        const trackWidth = offsets[offsets.length - 1];
        let maxIndex = 0;
        for (let index = 0; index < offsets.length; index++) {
            const offset = offsets[index];
            const remainder = trackWidth - offset;
            /**
             * If the remainder is less than the container width, then the last item is within view.
             * Floor the values to avoid any partial pixels causing issues
             */
            if (Math.ceil(remainder) < Math.floor(containerWidth)) {
                maxIndex = index;
                break;
            }
        }
        // Remove peeking amount from end of carousel
        offsets[maxIndex] = offsets[maxIndex] - params.paddingOffset ?? 0;
        // Initialise the carousel state.
        dispatch({
            type: 'init',
            payload: { offsets, maxIndex, count: params.count, paddingOffset: params.paddingOffset },
        });
    }
    /**
     * Go to the next item.
     */
    function goToNext(wrap) {
        dispatch({ type: 'next', payload: { wrap: wrap ?? false } });
    }
    /**
     * Go to the previous item.
     */
    function goToPrev(wrap) {
        dispatch({ type: 'prev', payload: { wrap: wrap ?? false } });
    }
    /**
     * Go to the target index.
     */
    function goToIndex(index) {
        dispatch({ type: 'goto', payload: index });
    }
    /**
     * Go to the target index.
     */
    function reinitialise(count, paddingOffset) {
        dispatch({ type: 'reinitialise', payload: { count, paddingOffset } });
    }
    /**
     * Get the number of items that are visible in the carousel.
     */
    function getVisibleItemCount() {
        if (!refTrack.current || !refContainer.current)
            return 1;
        const containerWidth = refContainer.current.getBoundingClientRect().width;
        const items = Array.from(refTrack.current.children);
        let totalWidth = 0;
        let visibleCount = 0;
        for (const item of items) {
            totalWidth += item.getBoundingClientRect().width;
            if (totalWidth <= containerWidth) {
                visibleCount++;
            }
            else {
                break;
            }
        }
        return visibleCount || 1;
    }
    function carouselReducer(state, action) {
        switch (action.type) {
            case 'init':
                return {
                    activeIndex: 0,
                    maxIndex: action.payload.maxIndex,
                    offsets: action.payload.offsets,
                    count: action.payload.count,
                    paddingOffset: action.payload.paddingOffset,
                };
            case 'reinitialise':
                return {
                    ...state,
                    count: action.payload.count,
                    paddingOffset: action.payload.paddingOffset,
                };
            case 'goto':
                return { ...state, activeIndex: clamp(action.payload) };
            case 'next': {
                const step = params.scrollByVisibleCount ? getVisibleItemCount() : 1;
                const nextIndex = state.activeIndex + step;
                if (action.payload.wrap && state.activeIndex === state.maxIndex) {
                    return { ...state, activeIndex: 0 };
                }
                return { ...state, activeIndex: clamp(nextIndex) };
            }
            case 'prev': {
                const step = params.scrollByVisibleCount ? getVisibleItemCount() : 1;
                const prevIndex = state.activeIndex - step;
                if (action.payload.wrap && state.activeIndex === 0) {
                    return { ...state, activeIndex: state.maxIndex };
                }
                return { ...state, activeIndex: clamp(prevIndex) };
            }
            default:
                return state;
        }
        /** Clamp index to be within the min/max index range. */
        function clamp(value) {
            return doClamp(value, state.maxIndex);
        }
    }
}
